import { create } from 'zustand';

import {
  JobStatusResponse,
  MappingConfig,
  ValidationError,
  WizardStep,
} from '../utils/types';

// --- localStorage persistence ---

const ACTIVE_JOB_KEY = 'lead-import-active-job';

export interface PersistedJobState {
  jobID: string;
  fileID: string;
  filename: string;
  startedAt: string;
}

export function getPersistedJobState(): PersistedJobState | null {
  try {
    const raw = localStorage.getItem(ACTIVE_JOB_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      parsed.jobID &&
      parsed.fileID &&
      parsed.filename &&
      parsed.startedAt
    ) {
      return parsed as PersistedJobState;
    }
    return null;
  } catch {
    return null;
  }
}

function persistJobState(state: PersistedJobState): void {
  try {
    localStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify(state));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

function clearPersistedJobState(): void {
  try {
    localStorage.removeItem(ACTIVE_JOB_KEY);
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

// --- Store interface ---

export interface ImportWizardState {
  // Navigation
  currentStep: WizardStep;
  setStep: (step: WizardStep) => void;

  // File state
  fileID: string | null;
  filename: string | null;
  headers: string[];
  sampleData: Record<string, string>[];
  rowCount: number;
  sheets: string[];
  setFileData: (data: { fileID: string; filename: string }) => void;
  setAnalysisData: (data: {
    headers: string[];
    sampleData: Record<string, string>[];
    rowCount: number;
    sheets?: string[];
  }) => void;
  setSheets: (sheets: string[]) => void;

  // Mapping config
  mappingConfig: Partial<MappingConfig>;
  updateMappingConfig: (partial: Partial<MappingConfig>) => void;
  resetMappingConfig: () => void;

  // Validation
  validationErrors: ValidationError[];
  setValidationErrors: (errors: ValidationError[]) => void;

  // Job state
  jobID: string | null;
  jobStatus: JobStatusResponse | null;
  setJobID: (id: string) => void;
  setJobStatus: (status: JobStatusResponse) => void;

  // Save config
  saveConfigName: string | null;
  setSaveConfigName: (name: string | null) => void;

  // Reset
  resetWizard: () => void;
}

const initialState = {
  currentStep: 'upload' as WizardStep,
  fileID: null,
  filename: null,
  headers: [],
  sampleData: [],
  rowCount: 0,
  sheets: [] as string[],
  mappingConfig: {},
  validationErrors: [],
  jobID: null,
  jobStatus: null,
  saveConfigName: null,
};

export const useImportWizardStore = create<ImportWizardState>((set, get) => ({
  ...initialState,

  // Navigation
  setStep: (step: WizardStep) => {
    set({ currentStep: step });
  },

  // File state
  setFileData: (data: { fileID: string; filename: string }) => {
    set({ fileID: data.fileID, filename: data.filename });
  },

  setAnalysisData: (data: {
    headers: string[];
    sampleData: Record<string, string>[];
    rowCount: number;
    sheets?: string[];
  }) => {
    set({
      headers: data.headers,
      sampleData: data.sampleData,
      rowCount: data.rowCount,
      sheets: data.sheets || [],
    });
  },

  setSheets: (sheets: string[]) => {
    set({ sheets });
  },

  // Mapping config
  updateMappingConfig: (partial: Partial<MappingConfig>) => {
    const { mappingConfig } = get();
    set({ mappingConfig: { ...mappingConfig, ...partial } });
  },

  resetMappingConfig: () => {
    set({ mappingConfig: {} });
  },

  // Validation
  setValidationErrors: (errors: ValidationError[]) => {
    set({ validationErrors: errors });
  },

  // Job state
  setJobID: (id: string) => {
    const { fileID, filename } = get();
    set({ jobID: id });
    persistJobState({
      jobID: id,
      fileID: fileID || '',
      filename: filename || '',
      startedAt: new Date().toISOString(),
    });
  },

  setJobStatus: (status: JobStatusResponse) => {
    set({ jobStatus: status });
  },

  // Save config
  setSaveConfigName: (name: string | null) => {
    set({ saveConfigName: name });
  },

  // Reset
  resetWizard: () => {
    clearPersistedJobState();
    set({ ...initialState });
  },
}));
